import type { OnlineConfig, ProjectVM, StudentApiClient, Theme } from '@ael/core-api';
import {
  assertNeverForViewMode,
  OfflineStudentViewModes,
  OnlineStudentViewModes,
  StudentViewMode,
} from '../../../../../config/constants';
import { dynamicEnv } from '../../../../../config/dynamic/dynamicEnv';
import { throwOnUnset } from '../../../../../global/utils/throwOnUnset';
import { NullClient } from '../../../../store/middleware/tracking/clients/nullClient/nullClient';
import {
  getTrackingData,
  notifyMobileAppTrackedDataChanged,
} from '../../../../store/middleware/tracking/clients/offlineClient/mobileAppIntegration';
import { OfflineClient } from '../../../../store/middleware/tracking/clients/offlineClient/offlineClient';
import { ScormTrackingClient } from '../../../../store/middleware/tracking/clients/scorm/scormTrackingClient';
import { ServerClient } from '../../../../store/middleware/tracking/clients/serverClient/serverClient';
import { TinCanTrackingClient } from '../../../../store/middleware/tracking/clients/tincanClient/TinCanTrackingClient';
import { xApiWrapperFactory } from '../../../../store/middleware/tracking/clients/tincanClient/xApiWrapper';
import { IPersistenceTrackingClient } from '../../../../store/middleware/tracking/clients/trackingClient';
import { createStudentClient } from '@ael/core-api';
import { safeInitializationOfTrackingClient } from './trackingStartup';
import { useQuery } from '@tanstack/react-query';
import { provideToken } from 'src/libraries/TokenProvider';

export type PreInitResult = {
  apiClient: StudentApiClient;
  projectGuid: string;
  preInitData:
    | {
        branding: {
          watermark?: string | null | undefined;
          tagline?: string | null | undefined;
          logoUrl?: string | null | undefined;
        };
        cultureCode: string;
        theme: Theme;
      }
    | 'not_found'
    | null;
};

export const useApplicationPreInitQuery = (viewMode: OnlineStudentViewModes) => {
  return useQuery({
    queryKey: ['StartupPreInit'],
    queryFn: async () => {
      const backendUrl = throwOnUnset(dynamicEnv('BACKEND_URL'), 'BACKEND_URL missing.');
      const inlineConfig: OnlineConfig = {
        VIEW_MODE: viewMode,
        PROJECT_ID: dynamicEnv('PROJECT_ID') ?? null,
        PROJECT_GUID: throwOnUnset(dynamicEnv('PROJECT_GUID'), 'PROJECT_GUID missing.'),
        AUTH0_AUDIENCE: dynamicEnv('AUTH0_AUDIENCE') ?? null,
        AUTH0_CLIENT_ID: dynamicEnv('AUTH0_CLIENT_ID') ?? null,
        AUTH0_DOMAIN: dynamicEnv('AUTH0_DOMAIN') ?? null,
        AUTHENTICATION_TOKEN: dynamicEnv('AUTHENTICATION_TOKEN') ?? null,
      };

      const provideAuthToken = async () => await provideToken(backendUrl, inlineConfig);
      const apiClient = createStudentClient(backendUrl, provideAuthToken);
      const preInitData = await apiClient.getPreInit({ guid: inlineConfig.PROJECT_GUID, viewMode: viewMode });
      const result: PreInitResult = {
        apiClient,
        preInitData,
        projectGuid: inlineConfig.PROJECT_GUID,
      };
      return result;
    },
    gcTime: Infinity,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    throwOnError: true,
  });
};

export const useOnlineStudentStartupQuery = (
  preInitResult: PreInitResult | undefined,
  viewMode: OnlineStudentViewModes
) => {
  return useQuery({
    queryKey: ['StudentStartup'],
    queryFn: async () => {
      if (
        preInitResult?.preInitData === 'not_found' ||
        preInitResult?.preInitData === null ||
        preInitResult?.preInitData === undefined
      ) {
        return null;
      }

      const projectViewModel: ProjectVM = await preInitResult.apiClient.getProject({
        guid: preInitResult.projectGuid,
        source: viewMode,
      });

      const client = _getOnlineTrackingClient(preInitResult.apiClient, preInitResult.projectGuid, viewMode);
      const trackingResult = await safeInitializationOfTrackingClient(client);

      return { projectViewModel, trackingResult };
    },
    enabled:
      preInitResult !== undefined && preInitResult.preInitData !== 'not_found' && preInitResult.preInitData !== null,
    gcTime: Infinity,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    throwOnError: true,
  });
};

export const useOfflineStartupQuery = (viewMode: OfflineStudentViewModes, projectViewModel: ProjectVM) => {
  return useQuery({
    queryKey: ['StudentStartup'],
    queryFn: async () => {
      const client = _getOfflineTrackingClient(viewMode, projectViewModel);
      const trackingResult = await safeInitializationOfTrackingClient(client);

      return { projectViewModel, trackingResult };
    },
    gcTime: Infinity,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    throwOnError: true,
  });
};

function _getOnlineTrackingClient(
  apiClient: StudentApiClient,
  projectGuid: string,
  viewMode: OnlineStudentViewModes
): IPersistenceTrackingClient {
  switch (viewMode) {
    case StudentViewMode.SCORM:
      return new ScormTrackingClient();
    case StudentViewMode.Basic:
      return new ServerClient(apiClient, projectGuid);
    case StudentViewMode.Public:
    case StudentViewMode.Preview:
      return new NullClient();
    default:
      return assertNeverForViewMode(viewMode);
  }
}

const _getOfflineTrackingClient = (
  viewMode: OfflineStudentViewModes,
  projectViewModel: ProjectVM
): IPersistenceTrackingClient => {
  switch (viewMode) {
    case StudentViewMode.Offline:
      return new OfflineClient(getTrackingData(), notifyMobileAppTrackedDataChanged);
    case StudentViewMode.ExportedPackaged_NullClient:
      return new NullClient();
    case StudentViewMode.Offline_SCORM:
      return new ScormTrackingClient();
    case StudentViewMode.Offline_TinCan:
    case StudentViewMode.TinCan: {
      const xApiBaseUrl: string = 'https://panomio.com';
      const courseGuid = projectViewModel.additionalSettings.guid; // Identifies for tincan storage
      const xApi = xApiWrapperFactory(courseGuid, xApiBaseUrl);
      const courseData = { guid: courseGuid, name: projectViewModel.config.name };
      return new TinCanTrackingClient(courseData, xApi, xApiBaseUrl);
    }
    default:
      return assertNeverForViewMode(viewMode);
  }
};
